import React, { useEffect, useState } from 'react';
import { Card, CardContent, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, Button, Checkbox, FormControlLabel, Popover, IconButton, Tooltip } from '@mui/material';
import { PiSortDescendingLight, PiSortAscendingLight } from "react-icons/pi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { MdSettings, MdDragIndicator, MdFileDownload } from "react-icons/md";
import { SelectChangeEvent } from '@mui/material/Select';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import authService from "../../../utils/authService";
import * as XLSX from 'xlsx';
import { compareTimeliness, isTimelinessSortField } from "../../../utils/timelinessSortUtils";
import { compareNumericScore, compareMaybeNumeric, isScoreSortField } from "../../../utils/numericSortUtils";

interface Project {
  batch_project_id: number;
  batch_project_name: string;
  project_id: number;
}

interface FiltersData {
  Courses: string[];
  batchs: {
    [course: string]: string[];
  };
  subjects: {
    [course: string]: {
      [batch: string]: string[];
    };
  };
  projects: {
    [course: string]: {
      [batch: string]: Project[];
    };
  };
}

interface Student {
  ID: string;
  Name: string;
  USN: string;
  Student_Type: string;
  College: string;
  Branch: string;
  Mobile: string;
  Score: string;
  "Score_%": string;
  Catogory: string;
  College_Rank: number;
  Overall_Rank: number;
  Email: string;
  CGPA: string | null;
  No_of_hours: string | null;
  no_of_hours_should_be_completed_by_current_day?: string | null;
  Course: string;
  Batch: string;
  Subject: string[];
  Attendance: string;
  Delays: number;
  Performance_Type: string;
  progress_percentage_of_should_be_completed_subtasks?: number;
  performance_percentage?: number;
  project_name?: string;
}

interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  sortable: boolean;
}

const AdminReport: React.FC = () => {
  const [filtersData, setFiltersData] = useState<FiltersData | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedBatch, setSelectedBatch] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string }>({ key: '', direction: '' });
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'USN', label: 'USN', visible: true, sortable: true },
    { key: 'Name', label: 'Name', visible: true, sortable: true },
    { key: 'Score', label: 'Score', visible: true, sortable: true },
    { key: 'Score_%', label: 'Score in %', visible: true, sortable: true },
    { key: 'CGPA', label: 'CGPA', visible: true, sortable: true },
    { key: 'Performance_Type', label: 'Timeliness', visible: true, sortable: true },
    { key: 'performance_percentage', label: 'Progress', visible: true, sortable: true },
    { key: 'College_Rank', label: 'College Rank', visible: true, sortable: true },
    { key: 'Overall_Rank', label: 'Overall Rank', visible: true, sortable: true },
    { key: 'No_of_hours', label: 'No of hours', visible: true, sortable: true },
    { key: 'Attendance', label: 'Attendance', visible: true, sortable: true },
    { key: 'Delays', label: 'Delay in days', visible: true, sortable: true },
    { key: 'Catogory', label: 'Category', visible: true, sortable: false },
    { key: 'College', label: 'College', visible: true, sortable: true },
    { key: 'Branch', label: 'Branch', visible: true, sortable: true },
    { key: 'ID', label: 'ID', visible: true, sortable: true },
    { key: 'Email', label: 'Email', visible: true, sortable: true },
    { key: 'Mobile', label: 'Phone no.', visible: true, sortable: true },
    { key: 'actions', label: 'Actions', visible: true, sortable: false }
  ]);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [totalTimeToBeSpent, setTotalTimeToBeSpent] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();

  // Save Reports filters to session storage whenever they change
  useEffect(() => {
    if (selectedCourse || selectedBatch || selectedSubject || selectedProject) {
      const filters = {
        course: selectedCourse,
        batch: selectedBatch,
        subject: selectedSubject,
        project: selectedProject
      };
      sessionStorage.setItem('dashboardFilters', JSON.stringify(filters));
    }
  }, [selectedCourse, selectedBatch, selectedSubject, selectedProject]);

  useEffect(() => {
    fetchFilters();
  }, []);

  // Restore dashboard filters when component mounts
  useEffect(() => {
    const savedFilters = sessionStorage.getItem('dashboardFilters');
    if (savedFilters && filtersData) {
      try {
        const filters = JSON.parse(savedFilters);
        
        // Restore course first
        if (filters.course && filtersData.Courses.includes(filters.course)) {
          setSelectedCourse(filters.course);
          
          // Restore batch if it exists for the selected course
          if (filters.batch && filtersData.batchs[filters.course]?.includes(filters.batch)) {
            setSelectedBatch(filters.batch);
            
            // Restore subject if it exists for the selected batch
            if (filters.subject && filtersData.subjects[filters.course]?.[filters.batch]?.includes(filters.subject)) {
              setSelectedSubject(filters.subject);
            }
            
            // Restore project if it exists for the selected batch
            if (filters.project && filtersData.projects[filters.course]?.[filters.batch]) {
              const project = filtersData.projects[filters.course][filters.batch].find(
                p => p.batch_project_id === filters.project.batch_project_id
              );
              if (project) {
                setSelectedProject(project);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error parsing saved dashboard filters:', error);
      }
    }
  }, [filtersData]); // Run when filtersData is loaded

  useEffect(() => {
    if (selectedCourse && selectedBatch && (selectedSubject || selectedProject)) {
      fetchStudents();
    }
  }, [selectedCourse, selectedBatch, selectedSubject, selectedProject]);

  useEffect(() => {
    setSelectedStudents(new Set());
  }, [selectedCourse, selectedBatch, selectedSubject, selectedProject, selectedCategory, searchQuery]);

  const fetchFilters = async () => {
    try {
      const userId = sessionStorage.getItem('Id');
      if (!userId) {
        console.error('User ID not found in session storage');
        return;
      }
      const response = await authService.get(`filters/analysis/`);
      setFiltersData(response.data);
      
      // Check if there are saved dashboard filters in session storage
      const savedFilters = sessionStorage.getItem('dashboardFilters');
      if (savedFilters) {
        try {
          const filters = JSON.parse(savedFilters);
          // Don't set defaults here - let the useEffect handle restoration
          return;
        } catch (error) {
          console.error('Error parsing saved dashboard filters:', error);
        }
      }
      
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      setIsLoading(true);
      const payload: any = {
        course: selectedCourse,
        batch: selectedBatch
      };

      if (selectedSubject) {
        payload.subject = selectedSubject;
        payload.batch_project_name = "";
        payload.project_id = null;
        payload.batch_project_id = null;
      } else if (selectedProject) {
        payload.subject = "";
        payload.batch_project_name = selectedProject.batch_project_name;
        payload.project_id = selectedProject.project_id;
        payload.batch_project_id = selectedProject.batch_project_id;
      }

      const response = await authService.post('analysis/', payload);
      const data = response.data.data;
      setTotalTimeToBeSpent(response?.data?.total_time_to_be_spent ?? '');
      if (data && Array.isArray(data)) {
        setAllStudents(data);
        setStudents(data);
        setError(null);
        setSelectedStudents(new Set());
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load student data. Please try refreshing the page.');
      setAllStudents([]);
      setStudents([]);
      setSelectedStudents(new Set());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    filterStudents();
  }, [selectedCourse, selectedBatch, selectedSubject, selectedProject, allStudents]);

  const handleCourseChange = (event: SelectChangeEvent<string>) => {
    const course = event.target.value;
    setSelectedCourse(course);
    setSelectedBatch('');
    setSelectedSubject('');
    setSelectedProject(null);
    setError(null);
  };

  const handleBatchChange = (event: SelectChangeEvent<string>) => {
    const batch = event.target.value;
    setSelectedBatch(batch);
    setSelectedSubject('');
    setSelectedProject(null);
    setError(null);
  };

  const handleSubjectChange = (event: SelectChangeEvent<string>) => {
    setSelectedSubject(event.target.value);
    setSelectedProject(null); // Clear project when subject is selected
    setError(null);
  };

  const handleProjectChange = (event: SelectChangeEvent<string>) => {
    const projectId = event.target.value;
    if (projectId && filtersData && filtersData.projects[selectedCourse] && filtersData.projects[selectedCourse][selectedBatch]) {
      const project = filtersData.projects[selectedCourse][selectedBatch].find(
        p => p.batch_project_id.toString() === projectId
      );
      setSelectedProject(project || null);
      setSelectedSubject(''); // Clear subject when project is selected
    } else {
      setSelectedProject(null);
    }
    setError(null);
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setSelectedCategory(event.target.value);
    setError(null);
  };

  const handleColumnVisibilityChange = (columnKey: string) => {
    setColumns(prevColumns => 
      prevColumns.map(col => 
        col.key === columnKey 
          ? { ...col, visible: !col.visible }
          : col
      )
    );
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchor(null);
  };

  const handleDragStart = (e: React.DragEvent, columnKey: string) => {
    setDraggedColumn(columnKey);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, columnKey: string) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== columnKey) {
      setDragOverColumn(columnKey);
    }
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetColumnKey: string) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== targetColumnKey) {
      const draggedIndex = columns.findIndex(col => col.key === draggedColumn);
      const targetIndex = columns.findIndex(col => col.key === targetColumnKey);
      
      const newColumns = [...columns];
      const [draggedItem] = newColumns.splice(draggedIndex, 1);
      newColumns.splice(targetIndex, 0, draggedItem);
      
      setColumns(newColumns);
    }
    setDraggedColumn(null);
    setDragOverColumn(null);
    setIsDragging(false);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
    setIsDragging(false);
  };

  const filterStudents = () => {
    let filteredStudents = [...allStudents];

    if (selectedCourse) {
      filteredStudents = filteredStudents.filter(student => student.Course === selectedCourse);
    }

    if (selectedBatch) {
      filteredStudents = filteredStudents.filter(student => student.Batch === selectedBatch);
    }

    if (selectedSubject) {
      filteredStudents = filteredStudents.filter(student => student.Subject.includes(selectedSubject));
    }

    // setStudents(filteredStudents);
    setStudents(allStudents);

    // const uniqueCategories = Array.from(new Set(filteredStudents.map(student => student.Catogory)));
    // setCategories(uniqueCategories);
    const uniqueCategories = Array.from(new Set(allStudents.map(student => student.Catogory)));
    setCategories(uniqueCategories);

    // if (filteredStudents.length === 0) {
      if (allStudents.length === 0) {
      // setError('No student data found for the selected criteria.');
    }
  };

  const handleSort = (key: string) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedStudents = () => {
    let filtered = [...students];

    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(student => {
        return Object.values(student).some(value => {
          if (value === null || value === undefined) return false;
          return value.toString().toLowerCase().includes(query);
        });
      });
    }

    if (selectedCategory) {
      filtered = filtered.sort((a, b) => {
        if (a.Catogory === selectedCategory && b.Catogory !== selectedCategory) return -1;
        if (a.Catogory !== selectedCategory && b.Catogory === selectedCategory) return 1;
        return 0;
      });
    }

    if (sortConfig.key) {
      filtered = filtered.sort((a, b) => {
        const aVal = a[sortConfig.key as keyof Student];
        const bVal = b[sortConfig.key as keyof Student];
        const aEmpty = aVal === null || aVal === undefined;
        const bEmpty = bVal === null || bVal === undefined;

        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (bEmpty) return sortConfig.direction === 'ascending' ? 1 : -1;

        if (isTimelinessSortField(sortConfig.key)) {
          return compareTimeliness(
            aVal as string,
            bVal as string,
            sortConfig.direction
          );
        }

        if (isScoreSortField(sortConfig.key)) {
          return compareNumericScore(aVal, bVal, sortConfig.direction);
        }

        return compareMaybeNumeric(aVal, bVal, sortConfig.direction);
      });
    }

    return filtered;
  };

  const handleViewStudent = (student: Student) => {
    if (location.pathname.includes('admin')) {
      navigate('/admin/performance-analysis', { state: { studentInfo: student } });
    } else if (location.pathname.includes('trainer')) {
      navigate('/trainer/performance-analysis', { state: { studentInfo: student } });
    } else if (location.pathname.includes('coordinator')) {
      navigate('/coordinator/performance-analysis', { state: { studentInfo: student } });
    } else {
      navigate("/", { state: { studentInfo: student } });
    }
  };

  const handleExportToExcel = () => {
    const dataToExport = getVisibleSelectedCount() > 0 
      ? sortedStudents().filter(student => selectedStudents.has(student.ID))
      : sortedStudents();
    const firstStudent = dataToExport[0];
    const progressHeaderLabel =
      firstStudent?.progress_percentage_of_should_be_completed_subtasks != null
        ? `Progress (${firstStudent.progress_percentage_of_should_be_completed_subtasks}%)`
        : "Progress";

    // Filter data to only include visible columns
    const visibleColumns = columns.filter(col => col.visible && col.key !== 'actions');
    const filteredData = dataToExport.map(student => {
      const filteredStudent: any = {};
      visibleColumns.forEach(column => {
        if (column.key === 'No_of_hours') {
          const noHours = student.No_of_hours ?? '';
          const noOfHoursHeader = `${column.label} (${totalTimeToBeSpent || '--'})`;
          filteredStudent[noOfHoursHeader] = noHours || '--';
        } else if (column.key === 'performance_percentage') {
          filteredStudent[progressHeaderLabel] =
            student.performance_percentage != null
              ? `${student.performance_percentage}%`
              : '--';
        } else if (column.key === 'Score' || column.key === 'Score_%') {
          filteredStudent[column.label] = student[column.key as keyof Student] === '--/--' 
            ? '--' 
            : student[column.key as keyof Student];
        } else if (column.key === 'CGPA') {
          filteredStudent[column.label] =
            student[column.key as keyof Student] !== null &&
            student[column.key as keyof Student] !== ""
              ? student[column.key as keyof Student]
              : "--";
        } else {
          filteredStudent[column.label] = student[column.key as keyof Student];
        }
      });
      return filteredStudent;
    });
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "student_report.xlsx");
  };

  const handleSelectStudent = (studentId: string) => {
    setSelectedStudents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const getVisibleStudentIds = () => sortedStudents().map(student => student.ID);

  const getVisibleSelectedCount = () => {
    const visibleStudentIds = getVisibleStudentIds();
    return visibleStudentIds.filter(id => selectedStudents.has(id)).length;
  };

  const handleSelectAll = () => {
    const visibleStudentIds = getVisibleStudentIds();
    const allVisibleSelected = visibleStudentIds.length > 0 &&
      visibleStudentIds.every(id => selectedStudents.has(id));

    if (allVisibleSelected) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(visibleStudentIds));
    }
  };

  return (
    <div className='container-fluid p-0 pe-2' style={{ marginTop: '12px' }}>
      <Card>
        <CardContent style={{ height: `calc(100vh - 90px)`, overflow: 'hidden', padding: '16px', display: 'flex', flexDirection: 'column' }}>
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            mb={1}
            style={{ flexShrink: 0 }}
          >
            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}
            >
              <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                <InputLabel style={{ fontSize: '14px', fontFamily: 'inherit' }}>Course</InputLabel>
                <Select
                  value={selectedCourse}
                  onChange={handleCourseChange}
                  label="Course"
                  disabled={isLoading}
                  style={{ 
                    height: '36px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                >
                  <MenuItem value="" style={{ fontSize: '14px', fontFamily: 'inherit' }}>Select Course</MenuItem>
                  {filtersData?.Courses.map(course => (
                    <MenuItem key={course} value={course} style={{ fontSize: '14px', fontFamily: 'inherit' }}>{course}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                <InputLabel style={{ fontSize: '14px', fontFamily: 'inherit' }}>Batch</InputLabel>
                <Select
                  value={selectedBatch}
                  onChange={handleBatchChange}
                  label="Batch"
                  disabled={!selectedCourse || isLoading}
                  style={{ 
                    height: '36px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                >
                  <MenuItem value="" style={{ fontSize: '14px', fontFamily: 'inherit' }}>Select Batch</MenuItem>
                  {filtersData?.batchs[selectedCourse]?.map(batch => (
                    <MenuItem key={batch} value={batch} style={{ fontSize: '14px', fontFamily: 'inherit' }}>{batch}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                <InputLabel style={{ fontSize: '14px', fontFamily: 'inherit' }}>Subject</InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={handleSubjectChange}
                  label="Subject"
                  disabled={!selectedBatch || isLoading}
                  style={{ 
                    height: '36px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                >
                  <MenuItem value="" style={{ fontSize: '14px', fontFamily: 'inherit' }}>Select Subject</MenuItem>
                  {filtersData?.subjects[selectedCourse]?.[selectedBatch]?.map(subject => (
                    <MenuItem key={subject} value={subject} style={{ fontSize: '14px', fontFamily: 'inherit' }}>{subject}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                <InputLabel style={{ fontSize: '14px', fontFamily: 'inherit' }}>Project</InputLabel>
                <Select
                  value={selectedProject?.batch_project_id.toString() || ''}
                  onChange={handleProjectChange}
                  label="Project"
                  disabled={!selectedBatch || isLoading}
                  style={{ 
                    height: '36px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                >
                  <MenuItem value="" style={{ fontSize: '14px', fontFamily: 'inherit' }}>Select Project</MenuItem>
                  {filtersData?.projects[selectedCourse]?.[selectedBatch]?.map(project => (
                    <MenuItem key={project.batch_project_id} value={project.batch_project_id.toString()} style={{ fontSize: '14px', fontFamily: 'inherit' }}>
                      {project.batch_project_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FormControl variant="outlined" size="small">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    minWidth: '200px',
                    height: '36px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                />
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExportToExcel}
                disabled={sortedStudents().length === 0}
                startIcon={<MdFileDownload />}
                style={{
                  height: '36px',
                  fontSize: '12px',
                  textTransform: 'none',
                  border: '1px solid #ccc',
                  color: '#666',
                  whiteSpace: 'nowrap',
                }}
              >
                Export {getVisibleSelectedCount() > 0 ? `(${getVisibleSelectedCount()})` : ''}
              </Button>
              <IconButton 
                onClick={handleSettingsClick}
                size="small"
                style={{ 
                  border: '1px solid #ccc', 
                  borderRadius: '4px',
                  padding: '6px',
                  height: '36px',
                  width: '36px'
                }}
              >
                <MdSettings size={18} />
              </IconButton>
            </Box>
          </Box>

          <TableContainer component={Paper} style={{ marginTop: '8px', flexGrow: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    style={{ 
                      backgroundColor: '#f5f5f5',
                      borderRight: '1px solid #e0e0e0',
                      fontWeight: 600,
                      fontSize: '12px',
                      padding: '8px 12px',
                      minWidth: '50px',
                      textAlign: 'center',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10
                    }}
                  >
                    {(() => {
                      const visibleCount = sortedStudents().length;
                      const visibleSelectedCount = getVisibleSelectedCount();
                      return (
                    <Checkbox
                      checked={visibleCount > 0 && visibleSelectedCount === visibleCount}
                      indeterminate={visibleSelectedCount > 0 && visibleSelectedCount < visibleCount}
                      onChange={handleSelectAll}
                      size="small"
                    />
                      );
                    })()}
                  </TableCell>
                  {columns.filter(col => col.visible).map((column) => (
                    column.key === 'Catogory' ? (
                      <TableCell 
                        key={column.key} 
                        draggable
                        onDragStart={(e) => handleDragStart(e, column.key)}
                        onDragOver={(e) => handleDragOver(e, column.key)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, column.key)}
                        onDragEnd={handleDragEnd}
                        style={{ 
                          whiteSpace: 'nowrap',
                          backgroundColor: dragOverColumn === column.key ? '#e3f2fd' : 
                                          draggedColumn === column.key ? '#f5f5f5' : '#f5f5f5',
                          borderRight: '1px solid #e0e0e0',
                          fontWeight: 600,
                          fontSize: '12px',
                          padding: '8px 12px',
                          minWidth: '120px',
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          cursor: isDragging ? 'grabbing' : 'grab',
                          opacity: draggedColumn === column.key ? 0.5 : 1,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <Box display="flex" alignItems="center">
                          <Tooltip title="Drag to reorder">
                            <MdDragIndicator 
                              size={16} 
                              style={{ 
                                marginRight: '4px', 
                                color: '#666',
                                cursor: 'grab'
                              }} 
                            />
                          </Tooltip>
                          <span style={{ marginRight: '8px', color: '#666' }}>{column.label}</span>
                          <FormControl variant="outlined" size="small" style={{ minWidth: 80 }}>
                            <Select
                              value={selectedCategory}
                              onChange={handleCategoryChange}
                              displayEmpty
                              disabled={isLoading}
                              style={{ height: '28px', fontSize: '11px' }}
                            >
                              <MenuItem value="">All</MenuItem>
                              {categories.map(category => (
                                <MenuItem key={category} value={category}>{category}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </TableCell>
                    ) : column.key === 'actions' ? (
                      <TableCell 
                        key={column.key}
                        draggable
                        onDragStart={(e) => handleDragStart(e, column.key)}
                        onDragOver={(e) => handleDragOver(e, column.key)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, column.key)}
                        onDragEnd={handleDragEnd}
                        style={{ 
                          backgroundColor: dragOverColumn === column.key ? '#e3f2fd' : 
                                          draggedColumn === column.key ? '#f5f5f5' : '#f5f5f5',
                          fontWeight: 600,
                          fontSize: '12px',
                          padding: '8px 12px',
                          minWidth: '80px',
                          textAlign: 'center',
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          cursor: isDragging ? 'grabbing' : 'grab',
                          opacity: draggedColumn === column.key ? 0.5 : 1,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <Tooltip title="Drag to reorder">
                            <MdDragIndicator 
                              size={16} 
                              style={{ 
                                marginRight: '4px', 
                                color: '#666',
                                cursor: 'grab'
                              }} 
                            />
                          </Tooltip>
                          {column.label}
                        </Box>
                      </TableCell>
                    ) : (
                      <TableCell 
                        key={column.key} 
                        draggable
                        onDragStart={(e) => handleDragStart(e, column.key)}
                        onDragOver={(e) => handleDragOver(e, column.key)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, column.key)}
                        onDragEnd={handleDragEnd}
                        onClick={() => column.sortable && handleSort(column.key)} 
                        style={{ 
                          cursor: isDragging ? 'grabbing' : (column.sortable ? 'pointer' : 'grab'), 
                          whiteSpace: 'nowrap',
                          backgroundColor: dragOverColumn === column.key ? '#e3f2fd' : 
                                          draggedColumn === column.key ? '#f5f5f5' : '#f5f5f5',
                          borderRight: '1px solid #e0e0e0',
                          fontWeight: 600,
                          fontSize: '12px',
                          padding: '8px 12px',
                          minWidth: column.key === 'ID' ? '80px' : 
                                   column.key === 'USN' ? '120px' :
                                   column.key === 'Name' ? '120px' :
                                   column.key === 'Email' ? '180px' :
                                   column.key === 'Mobile' ? '120px' : '100px',
                          position: 'sticky',
                          top: 0,
                          zIndex: 10,
                          opacity: draggedColumn === column.key ? 0.5 : 1,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center">
                            <Tooltip title="Drag to reorder">
                              <MdDragIndicator 
                                size={16} 
                                style={{ 
                                  marginRight: '4px', 
                                  color: '#666',
                                  cursor: 'grab'
                                }} 
                              />
                            </Tooltip>
                            <span style={{ color: '#666' }}>
                              {column.key === 'performance_percentage' && sortedStudents().length > 0
                                ? `${column.label} (${sortedStudents()[0].progress_percentage_of_should_be_completed_subtasks !== null && sortedStudents()[0].progress_percentage_of_should_be_completed_subtasks !== undefined ? `${sortedStudents()[0].progress_percentage_of_should_be_completed_subtasks}%` : '--'})`
                                : column.key === 'No_of_hours'
                                ? `${column.label} (${totalTimeToBeSpent || '--'})`
                                : column.label}
                            </span>
                          </Box>
                          {column.sortable && (
                            <span style={{ marginLeft: '4px' }}>
                              {sortConfig.key === column.key ? 
                                (sortConfig.direction === 'ascending' ? 
                                  <PiSortAscendingLight size={16} /> : 
                                  <PiSortDescendingLight size={16} />
                                ) : 
                                <HiOutlineDotsVertical size={16} />
                              }
                            </span>
                          )}
                        </Box>
                      </TableCell>
                    )
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {error ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.filter(col => col.visible).length + 1}
                      align="center"
                      style={{ padding: '20px' }}
                    >
                      <Typography color="error" variant="body1">
                        {error}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : isLoading ? (
                  <TableRow>
                    <TableCell colSpan={columns.filter(col => col.visible).length + 1} align="center" style={{ padding: '20px' }}>
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </TableCell>
                  </TableRow>
                ) : sortedStudents().length > 0 ? (
                  sortedStudents().map((student, index) => (
                    <TableRow 
                      key={student.ID || index}
                      style={{ 
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa'
                      }}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f0f0f0',
                          cursor: 'pointer'
                        }
                      }}
                    >
                      <TableCell 
                        style={{ 
                          padding: '6px 12px',
                          textAlign: 'center',
                          borderRight: '1px solid #e0e0e0'
                        }}
                      >
                        <Checkbox
                          checked={selectedStudents.has(student.ID)}
                          onChange={() => handleSelectStudent(student.ID)}
                          size="small"
                        />
                      </TableCell>
                      {columns.filter(col => col.visible).map((column) => (
                        column.key === 'actions' ? (
                          <TableCell 
                            key={column.key}
                            style={{ 
                              padding: '6px 12px',
                              textAlign: 'center',
                              borderRight: '1px solid #e0e0e0'
                            }}
                          >
                            <Button 
                              variant="contained" 
                              color="primary" 
                              size="small"
                              onClick={() => handleViewStudent(student)}
                              style={{ 
                                fontSize: '11px',
                                padding: '4px 12px',
                                minWidth: '60px'
                              }}
                            >
                              VIEW
                            </Button>
                          </TableCell>
                        ) : (
                          <TableCell 
                            key={column.key}
                            style={{ 
                              padding: '6px 12px',
                              fontSize: '12px',
                              borderRight: '1px solid #e0e0e0',
                              color: '#333',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {column.key === 'No_of_hours'
                            ? (student.No_of_hours != null && student.No_of_hours !== ''
                                ? student.No_of_hours
                                : '--')
                            : column.key === 'performance_percentage'
                            ? (student.performance_percentage != null ? `${student.performance_percentage}%` : '--')
                            : column.key === 'ID' || column.key === 'USN' || column.key === 'Score' || column.key === 'Score_%'
                            ? <span style={{ fontWeight: 600, color: '#1976d2' }}>
                                {column.key === 'Score' || column.key === 'Score_%'
                                  ? (student[column.key as keyof Student] === '--/--' 
                                      ? '--' 
                                      : student[column.key as keyof Student])
                                  : student[column.key as keyof Student]
                                }
                              </span>
                            : column.key === 'CGPA'
                            ? <span style={{ fontWeight: 600, color: '#4caf50' }}>
                                {student[column.key as keyof Student] !== null &&
                                student[column.key as keyof Student] !== ""
                                  ? student[column.key as keyof Student]
                                  : "--"}
                              </span>
                            : column.key === 'Performance_Type'
                            ? <span style={{ 
                                fontWeight: 600, 
                                color: student[column.key as keyof Student] === 'ahead' ? '#4caf50' : 
                                      student[column.key as keyof Student] === 'laggards' ? '#f44336' : '#ff9800',
                                textTransform: 'capitalize'
                              }}>
                                {student[column.key as keyof Student]}
                              </span>
                            : column.key === 'Name'
                            ? <span style={{ fontWeight: 500, color: '#333' }}>{student[column.key as keyof Student]}</span>
                            : column.key === 'Email'
                            ? <span style={{ color: '#666', fontSize: '11px' }}>{student[column.key as keyof Student]}</span>
                            : student[column.key as keyof Student]
                          }
                          </TableCell>
                        )
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell 
                      colSpan={columns.filter(col => col.visible).length + 1} 
                      align="center"
                      style={{ 
                        padding: '20px',
                        color: '#666',
                        fontSize: '14px',
                        fontStyle: 'italic'
                      }}
                    >
                      {selectedCourse && selectedBatch && (selectedSubject || selectedProject)
                        ? 'No student data available for the selected criteria'
                        : 'Please select course, batch, and subject/project to view student data'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Column Settings Popover */}
          <Popover
            open={Boolean(settingsAnchor)}
            anchorEl={settingsAnchor}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box sx={{ p: 2, minWidth: 200 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Column Visibility
              </Typography>
              {columns.map((column) => (
                <FormControlLabel
                  key={column.key}
                  control={
                    <Checkbox
                      checked={column.visible}
                      onChange={() => handleColumnVisibilityChange(column.key)}
                      disabled={column.key === 'actions'} // Always keep actions column visible
                    />
                  }
                  label={column.label}
                  sx={{ display: 'block', mb: 1 }}
                />
              ))}
            </Box>
          </Popover>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminReport; 